import React, { useState } from 'react';
import { Briefcase, PiggyBank, ChevronDown, ChevronRight, FileText, Loader } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import useFinanceData from './useFinanceData';
import useGameStore from '../../store/gameStore';
import useLicenseStore from '../../store/licenseStore';
import ExerciseStatementsPanel, { downloadStatementPDF } from './ExerciseStatementsPanel';
import { buildExerciseStatement, POSTE_LABELS } from '../../utils/exerciseStatements';

// 21/09/2026 — bouton « Bilan PDF » par exercice clos (demande user).
function YearStatementButton({ report }) {
  const [busy, setBusy] = useState(false);
  const companyName = useGameStore((s) => s.company?.name);
  const holderName = useLicenseStore((s) => s.holderName);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadStatementPDF(buildExerciseStatement(report), { companyName, holderName });
    } catch (err) {
      console.error('[finance] year statement PDF failed', err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      title={`Télécharger le bilan complet de l'exercice ${report.year} (PDF)`}
      className="flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-200 border border-sky-500/30 hover:bg-sky-500/25 disabled:opacity-50 font-semibold uppercase tracking-wider transition-colors"
      data-testid={`year-statement-pdf-${report.year}`}
    >
      {busy ? <Loader className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
      Bilan PDF
    </button>
  );
}

function YearExpenseBreakdown({ months, totalExpenses, year }) {
  const [open, setOpen] = useState(false);
  const withData = (months || []).filter(m => m.expensesByPoste);
  if (withData.length === 0) return null;
  const totals = {};
  withData.forEach(m => {
    Object.entries(m.expensesByPoste).forEach(([k, v]) => {
      totals[k] = (totals[k] || 0) + (v || 0);
    });
  });
  const rows = Object.keys(POSTE_LABELS)
    .filter(k => (totals[k] || 0) > 0)
    .map(k => ({ key: k, label: POSTE_LABELS[k], value: totals[k] }))
    .sort((a, b) => b.value - a.value);
  if (rows.length === 0) return null;
  const sum = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="mt-2 border-t border-ink-600 pt-2" data-testid={`expense-breakdown-${year}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
        data-testid={`expense-breakdown-toggle-${year}`}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Détail des charges par poste
        {withData.length < 12 && <span className="text-slate-600">({withData.length} mois tracés)</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          {rows.map(r => (
            <div key={r.key} className="flex items-center gap-2 text-[11px]" data-testid={`expense-poste-${year}-${r.key}`}>
              <span className="text-slate-400 w-56 truncate">{r.label}</span>
              <div className="flex-1 h-1.5 bg-ink-700 rounded overflow-hidden">
                <div className="h-full bg-danger/60 rounded" style={{ width: `${Math.max(2, Math.round((r.value / sum) * 100))}%` }} />
              </div>
              <span className="font-mono text-slate-300 w-24 text-right">{formatMoney(r.value)}</span>
              <span className="font-mono text-slate-500 w-10 text-right">{Math.round((r.value / (totalExpenses || sum)) * 100)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Finance — onglet Historique annuel. Liste les rapports du Conseil
 * d'administration (un par exercice clôturé). Affiche CA, charges, marge,
 * meilleur / pire mois et la résolution votée. Extracted from
 * Finance.jsx on 28/05/2026.
 */
export default function HistoryTab() {
  const { yearlyReports } = useFinanceData();

  return (
    <div className="space-y-3" data-testid="finance-history-tab">
      <div className="bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-lg p-3">
        <p className="text-xs text-slate-300 italic">
          Chaque fin d&apos;année comptable (12 mois), le <strong>Conseil d&apos;administration</strong> se réunit
          pour valider les comptes. Tous les bilans clôturés sont consultables ici.
        </p>
      </div>

      {/* 21/09/2026 — Bilans d'exercice : aperçu cumulé + export PDF (demande user) */}
      <ExerciseStatementsPanel />

      {(!yearlyReports || yearlyReports.length === 0) ? (
        <div className="bg-ink-800 border border-ink-600 rounded-lg p-8 text-center">
          <Briefcase className="w-8 h-8 text-slate-500 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Aucun exercice clôturé pour le moment.</p>
          <p className="text-xs text-slate-500 mt-1">Le premier Conseil aura lieu après 12 mois de jeu.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...yearlyReports].reverse().map(r => {
            const profit = r.netResult >= 0;
            const margin = r.totalRevenue > 0 ? Math.round((r.netResult / r.totalRevenue) * 100) : 0;
            return (
              <div
                key={r.year}
                className={`bg-ink-800 rounded-lg border p-4 ${profit ? 'border-money/30' : 'border-danger/30'}`}
                data-testid={`yearly-report-${r.year}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-emerald-900/30">
                      <Briefcase className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-200">Exercice {r.year}</h4>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">Conseil d&apos;administration</p>
                    </div>
                  </div>
                  <div className={`text-right ${profit ? 'text-money' : 'text-danger'}`}>
                    <p className="text-xl font-black font-mono">
                      {profit ? '+' : ''}{formatMoney(r.netResult)}
                    </p>
                    <p className="text-[10px] opacity-80 font-mono">Marge {margin >= 0 ? '+' : ''}{margin}%</p>
                    <div className="mt-1 flex justify-end"><YearStatementButton report={r} /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-ink-700/30 rounded p-2">
                    <p className="text-[10px] text-slate-500">CA annuel</p>
                    <p className="text-money font-bold font-mono">{formatMoney(r.totalRevenue)}</p>
                  </div>
                  <div className="bg-ink-700/30 rounded p-2">
                    <p className="text-[10px] text-slate-500">Charges</p>
                    <p className="text-danger font-bold font-mono">{formatMoney(r.totalExpenses)}</p>
                  </div>
                  <div className="bg-ink-700/30 rounded p-2">
                    <p className="text-[10px] text-slate-500">Δ Trésorerie</p>
                    <p className={`${r.treasuryDelta >= 0 ? 'text-money' : 'text-danger'} font-bold font-mono`}>
                      {r.treasuryDelta >= 0 ? '+' : ''}{formatMoney(r.treasuryDelta)}
                    </p>
                  </div>
                  <div className="bg-ink-700/30 rounded p-2">
                    <p className="text-[10px] text-slate-500">Flotte / Équipe</p>
                    <p className="text-slate-300 font-bold">{r.vehiclesTotal} véh. · {r.driversTotal} chauf.</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                  <span className="px-2 py-0.5 rounded bg-money/10 text-money font-mono">
                    Meilleur mois M{r.bestMonth.month} : {r.bestMonth.netResult >= 0 ? '+' : ''}{formatMoney(r.bestMonth.netResult)}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-danger/10 text-danger font-mono">
                    Mois dur M{r.worstMonth.month} : {r.worstMonth.netResult >= 0 ? '+' : ''}{formatMoney(r.worstMonth.netResult)}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-ink-700 text-slate-400 font-mono">
                    Réputation {r.reputation} · Notoriété {r.notoriety}
                  </span>
                  {r.resolution && (
                    <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono" data-testid={`yearly-resolution-${r.year}`}>
                      Conseil : {({
                        dividend: 'Dividende voté',
                        investment: "Plan d'investissement",
                        restructuring: 'Restructuration',
                        statu_quo: 'Statu quo',
                      })[r.resolution.type] || r.resolution.type}
                    </span>
                  )}
                </div>
                {/* 22/07/2026 — détail des charges par poste (retour user) */}
                <YearExpenseBreakdown months={r.months} totalExpenses={r.totalExpenses} year={r.year} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
